import {
    CustomTimeframeOptions,
    CustomTimeframeValue,
    getTimeframe,
    isCustomTimeframe,
    isEnumTimeframe,
    localeUnitToCompact,
    TimeframeEnumValue,
    UnresolvedTimeframe,
    WEEK_STARTS_ON_DEFAULT,
    YEAR_STARTS_ON_DEFAULT
} from '@squaredup/timeframes';
import { formatDate, FormatDateOptions, standardDatePatterns } from '@squaredup/utilities';
import { Day, isValid } from 'date-fns';
import { RelativeDateTimeValue, resolveCustomTimeframeFormValues, TimeframeFormData } from './timeframeForm';

/**
 * Converts a RelativeDateTimeValue to a CustomTimeframeValue.
 * @param field
 * @returns CustomTimeframeValue, i.e. 'now', '2023-10-01T00:00:00Z', 1696118400000, '-1d', '1w@w-1d'
 */
export const toCustomTimeframeValue = (field: RelativeDateTimeValue): string | undefined => {
    if (field.type === 'fixed' && field.date) {
        if (isValid(field.date)) {
            return field.date.toISOString();
        }

        return undefined;
    }

    const snappingUnit = localeUnitToCompact(field.unit || 'hour');

    if (field.type === 'now' && field.modifier === 'exact') {
        return 'now';
    }

    let snapping = '';

    if (field.type === 'start') {
        snapping = '@' + snappingUnit;
    } else if (field.type === 'end') {
        snapping = '@' + snappingUnit + '+1' + snappingUnit;
    } else if (field.type === 'startPrevious') {
        snapping = '@' + snappingUnit + '-1' + snappingUnit;
    } else if (field.type === 'endPrevious') {
        // not a fan of this as equivalent to `start` minus 1, but needed for 'End of previous' UI option.
        snapping = '@' + snappingUnit + '-';
    }

    // if no modifier is provided, just uses snapping
    if (field.modifier === 'exact') {
        return snapping;
    }

    let modifier = '';
    const modifierUnit = localeUnitToCompact(field.modifierUnit || 'hour');
    if (field.modifier === 'minus') {
        modifier = '-' + field.modifierValue + modifierUnit;
    } else if (field.modifier === 'plus') {
        modifier = '+' + field.modifierValue + modifierUnit;
    }

    return modifier + snapping;
};

/**
 * Converts week and year start field options to CustomTimeframeOptions.
 * Will return undefined if no options are set or if they match the defaults.
 */
export const toCustomTimeframeOptions = (
    weekStartsOn?: TimeframeFormData['weekStartsOn'],
    yearStartsOn?: TimeframeFormData['yearStartsOn']
): CustomTimeframeOptions | undefined => {
    // no values set
    if (!weekStartsOn && !yearStartsOn?.day && !yearStartsOn?.month) {
        return undefined;
    }

    // Using only the default values, no need to return options.
    if (
        weekStartsOn === WEEK_STARTS_ON_DEFAULT &&
        yearStartsOn?.day === YEAR_STARTS_ON_DEFAULT.day &&
        yearStartsOn?.month === YEAR_STARTS_ON_DEFAULT.month
    ) {
        return undefined;
    }

    return {
        weekStartsOn: weekStartsOn as Day,
        yearStartsOn: yearStartsOn
    };
};

/**
 * Formats a timeframe preview for display.
 * i.e. '2023-10-01T00:00:00Z' -> '2023-10-01 00:00 (UTC +0100)'
 */
export const formatTimeframePreview = (isoDate: string, options?: FormatDateOptions) => {
    return formatDate(isoDate, {
        pattern: standardDatePatterns.short,
        appendUtcOffset: 'always',
        ...options
    });
};

/**
 * Returns a formatted preview of the timeframe based on start and end values.
 * Will return the values without rounding, so they are the absolute values resolved.
 */
export const getTimeframePreview = (
    start?: CustomTimeframeValue,
    end?: CustomTimeframeValue,
    options?: CustomTimeframeOptions,
    formatOptions?: FormatDateOptions
) => {
    const timeframe = getTimeframe(
        {
            start,
            end,
            type: 'custom',
            options
        },
        {
            sanitiseInterval: true,
            // avoid rounding the timeframe
            sanitiseTimeframe: false
        }
    );

    return {
        from: formatTimeframePreview(timeframe.start, formatOptions),
        to: formatTimeframePreview(timeframe.end, formatOptions)
    };
};

const defaultFormValue: TimeframeFormData = {
    from: {
        type: 'now',
        unit: 'hour',
        modifier: 'minus',
        modifierUnit: 'hour',
        modifierValue: 24
    },
    to: {
        type: 'now',
        modifier: 'exact',
        // prevents uncontrolled input error
        modifierValue: undefined
    },
    weekStartsOn: WEEK_STARTS_ON_DEFAULT,
    yearStartsOn: YEAR_STARTS_ON_DEFAULT
};

/**
 * Map timeframe enum to default values for the form.
 */
const getDefaultValueEnum = (
    timeframe: TimeframeEnumValue
): {
    from: RelativeDateTimeValue;
    to: RelativeDateTimeValue;
} => {
    let fromValue: RelativeDateTimeValue = defaultFormValue.from;
    let toValue: RelativeDateTimeValue = defaultFormValue.to;

    switch (timeframe) {
        case 'last1hour':
            fromValue = {
                type: 'now',
                unit: 'hour',
                modifier: 'minus',
                modifierUnit: 'hour',
                modifierValue: 1
            };
            break;
        case 'last12hours':
            fromValue = {
                type: 'now',
                unit: 'hour',
                modifier: 'minus',
                modifierUnit: 'hour',
                modifierValue: 12
            };
            break;
        case 'last24hours':
            fromValue = {
                type: 'now',
                unit: 'hour',
                modifier: 'minus',
                modifierUnit: 'hour',
                modifierValue: 24
            };
            break;
        case 'last7days':
            fromValue = {
                type: 'now',
                unit: 'day',
                modifier: 'minus',
                modifierUnit: 'day',
                modifierValue: 7
            };
            break;
        case 'last30days':
            fromValue = {
                type: 'now',
                unit: 'day',
                modifier: 'minus',
                modifierUnit: 'day',
                modifierValue: 30
            };
            break;
        case 'thisQuarter':
            fromValue = {
                type: 'start',
                unit: 'quarter',
                modifier: 'exact',
                modifierUnit: 'quarter',
                modifierValue: undefined
            };
            toValue = {
                type: 'end',
                unit: 'quarter',
                modifier: 'exact',
                modifierUnit: 'quarter',
                modifierValue: undefined
            };
            break;
        case 'lastQuarter':
            fromValue = {
                unit: 'quarter',
                type: 'startPrevious',
                modifier: 'exact',
                modifierUnit: 'quarter',
                modifierValue: undefined
            };
            toValue = {
                type: 'start',
                unit: 'quarter',
                modifier: 'exact',
                modifierUnit: 'quarter',
                modifierValue: undefined
            };
            break;
        case 'thisYear':
            fromValue = {
                unit: 'year',
                type: 'start',
                modifier: 'exact',
                modifierUnit: 'year',
                modifierValue: undefined
            };
            toValue = {
                type: 'end',
                unit: 'year',
                modifier: 'exact',
                modifierUnit: 'year',
                modifierValue: undefined
            };
            break;
        case 'lastYear':
            fromValue = {
                unit: 'year',
                type: 'startPrevious',
                modifier: 'exact',
                modifierUnit: 'year',
                modifierValue: undefined
            };
            toValue = {
                type: 'start',
                unit: 'year',
                modifier: 'exact',
                modifierUnit: 'year',
                modifierValue: undefined
            };
            break;
        case 'thisMonth':
            fromValue = {
                unit: 'month',
                type: 'start',
                modifier: 'exact',
                modifierUnit: 'month',
                modifierValue: undefined
            };
            toValue = {
                type: 'end',
                unit: 'month',
                modifier: 'exact',
                modifierUnit: 'month',
                modifierValue: undefined
            };
            break;
        case 'lastMonth':
            fromValue = {
                type: 'startPrevious',
                unit: 'month',
                modifier: 'exact',
                modifierUnit: 'month',
                modifierValue: undefined
            };
            toValue = {
                type: 'start',
                unit: 'month',
                modifier: 'exact',
                modifierUnit: 'month',
                modifierValue: undefined
            };
            break;
        default:
        // default case
    }

    return {
        from: fromValue,
        to: toValue
    };
};

export const getCustomTimeframeFormDefaults = (timeframe?: UnresolvedTimeframe): TimeframeFormData => {
    if (!timeframe) {
        return defaultFormValue;
    }

    if (typeof timeframe === 'string') {
        return { ...defaultFormValue, ...getDefaultValueEnum(timeframe) };
    }

    if (isEnumTimeframe(timeframe)) {
        return { ...defaultFormValue, ...getDefaultValueEnum(timeframe.enum as TimeframeEnumValue) };
    }

    if (isCustomTimeframe(timeframe)) {
        return {
            from: resolveCustomTimeframeFormValues(timeframe.start),
            to: resolveCustomTimeframeFormValues(timeframe.end),
            weekStartsOn: timeframe.options?.weekStartsOn ?? WEEK_STARTS_ON_DEFAULT,
            yearStartsOn: {
                day: timeframe.options?.yearStartsOn?.day ?? YEAR_STARTS_ON_DEFAULT.day,
                month: timeframe.options?.yearStartsOn?.month ?? YEAR_STARTS_ON_DEFAULT.month
            }
        };
    }

    return defaultFormValue;
};

/**
 * Check if given timeframe enum is active, based on timeframe passed in.
 */
export const isActiveTimeframe = (timeframeEnum: string, activeTimeframe?: UnresolvedTimeframe) => {
    if (!activeTimeframe) {
        return false;
    }

    if (typeof activeTimeframe === 'string') {
        return timeframeEnum === activeTimeframe;
    }

    if (activeTimeframe.type === 'enum' && 'enum' in activeTimeframe) {
        return activeTimeframe.enum === timeframeEnum;
    }

    return false;
};
